import { useEffect, useCallback, useRef } from 'react'
import { useUser } from '@clerk/clerk-react'
import {
  setAnalyticsUser,
  registerUserIdRetry,
  trackFeatureEnter,
  trackUserAction,
  trackEvent,
} from '../utils/analytics'
import { apiGetAuth } from '../api'

// Check if Clerk is configured
const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY

/**
 * Hook to sync the current authenticated user with analytics.
 * Call this once at app root level.
 *
 * @example
 * function App() {
 *   useAnalyticsUserSync()
 *   return <Routes />
 * }
 */
export function useAnalyticsUserSync() {
  // Only call useUser if Clerk is enabled (ClerkProvider is present)
  const userResult = clerkEnabled ? useUser() : { isSignedIn: false, user: null }
  const { isSignedIn, user } = userResult

  useEffect(() => {
    async function syncUser() {
      if (isSignedIn && user) {
        try {
          // Fetch the internal user ID from our API
          const response = await apiGetAuth<{ id: number }>('/user/me')
          setAnalyticsUser(response.id)
        } catch {
          // User not synced yet, that's okay
          setAnalyticsUser(null)
        }
      } else {
        setAnalyticsUser(null)
      }
    }

    // Register the sync function as a retry callback for analytics
    // This allows analytics to retry fetching user ID if needed
    registerUserIdRetry(syncUser)

    syncUser()
  }, [isSignedIn, user])
}

/**
 * Hook to track feature usage with automatic enter/exit timing.
 * Use this on feature pages to track time spent.
 *
 * @param featureName - Name of the feature (e.g., 'Value Bets', 'Bet Builder')
 *
 * @example
 * function ValueBetsPage() {
 *   useFeatureTracking('Value Bets')
 *   return <div>...</div>
 * }
 */
export function useFeatureTracking(featureName: string) {
  const cleanupRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    // Track feature enter and get cleanup function
    cleanupRef.current = trackFeatureEnter(featureName)

    // Cleanup: track feature exit
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [featureName])
}

/**
 * Hook that returns a function to track user actions.
 * Use this for tracking discrete actions within a feature.
 *
 * @example
 * function BetBuilder() {
 *   const trackAction = useTrackAction()
 *
 *   const handleAddLeg = () => {
 *     trackAction('builder_add_leg', { market: 'BTTS' })
 *     // ... add leg logic
 *   }
 * }
 */
export function useTrackAction() {
  return useCallback((action: string, details?: Record<string, any>) => {
    trackUserAction(action, details)
  }, [])
}

/**
 * Hook that returns a function to track generic events.
 * Use this for custom event tracking.
 *
 * @example
 * function SearchModal() {
 *   const track = useTrackEvent()
 *
 *   const handleSearch = (query: string) => {
 *     track('search', { query, results_count: results.length })
 *   }
 * }
 */
export function useTrackEvent() {
  return useCallback((eventName: string, properties?: Record<string, any>) => {
    trackEvent(eventName, properties)
  }, [])
}

/**
 * Feature names for consistent tracking across the app.
 */
export const FeatureNames = {
  VALUE_BETS: 'Value Bets',
  LIVE_TIPS: 'Live Tips',
  BET_BUILDER: 'Bet Builder',
  STRATEGIES: 'Strategies',
  DIXON_COLES: 'Dixon-Coles',
  CARDS_HUB: 'Cards Hub',
  GOALS_HUB: 'Goals Hub',
  PLAYER_STATS: 'Player Stats',
  REFEREES: 'Referees',
  MY_BETS: 'My Bets',
  FIXTURES: 'Fixtures',
  AI_PREDICTIONS: 'AI Predictions',
} as const

/**
 * Action names for consistent tracking across the app.
 */
export const ActionNames = {
  // Builder actions
  BUILDER_OPEN: 'builder_open',
  BUILDER_ADD_LEG: 'builder_add_leg',
  BUILDER_REMOVE_LEG: 'builder_remove_leg',
  BUILDER_SAVE: 'builder_save',

  // Bet actions
  BET_TRACK: 'bet_track',
  BET_SETTLE: 'bet_settle',

  // Strategy actions
  STRATEGY_CREATE: 'strategy_create',
  STRATEGY_RUN: 'strategy_run',
  STRATEGY_DELETE: 'strategy_delete',

  // Filter/Search actions
  FILTER_APPLY: 'filter_apply',
  SEARCH: 'search',

  // View actions
  FIXTURE_EXPAND: 'fixture_expand',
  TAB_SWITCH: 'tab_switch',
} as const
